/**
 * §12.8 HAPTICS — BONUS, NEVER LOAD-BEARING (12-Motion.dc.html):
 *
 * "navigator.vibrate on Android only; iOS Safari has no API and most of
 * this audience is on iOS. Three events get 10ms: play start, download
 * success, trim handle hitting a bound. Nothing else. Every one has a
 * visual equivalent that is the actual feedback, the haptic is a second
 * channel, never the only one. Suppressed under reduced motion, because
 * vestibular sensitivity and haptic sensitivity overlap."
 *
 * Three gates, all mandatory:
 *   1. `navigator.vibrate` must exist and be callable — absent on iOS
 *      Safari and most desktop browsers. Never throw where it's missing.
 *   2. Desktop is excluded outright — a coarse-pointer/touch check, not a
 *      viewport-width guess, since a touch laptop can still have a mouse.
 *   3. `prefers-reduced-motion: reduce` suppresses it entirely, matching
 *      §12.10's "Haptics: 10ms on three events → Suppressed entirely."
 *
 * Call sites (one per named event, nowhere else):
 *   - "play-start"       hooks/useAudioPlayer.ts, on the play() transition to playing
 *   - "download-success" hooks/useDownload.ts, on a completed download
 *   - "trim-bound"       the trim/scrub control, when a handle hits start/end
 */

export type HapticEvent = "play-start" | "download-success" | "trim-bound";

const HAPTIC_DURATION_MS = 10;

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    // Some embedded/test environments throw on unsupported media features.
    return false;
  }
}

/**
 * Desktop exclusion. `navigator.vibrate` existing is necessary but not
 * sufficient — some desktop browsers expose the method as a no-op stub.
 * `(pointer: coarse)` is the primary input's pointer precision, which is
 * the right question ("is the primary way this person taps a touchscreen")
 * rather than screen width, which a touch laptop would get wrong either way.
 */
function isPrimaryInputTouch(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
}

/**
 * Fires a 10ms vibration for one of the three named events, or does
 * nothing. Never throws: every gate below degrades to a silent no-op
 * rather than an error, because this is a bonus channel with a visual
 * equivalent that is the actual feedback (§12.8).
 */
export function triggerHaptic(_event: HapticEvent): void {
  if (typeof navigator === "undefined") return;
  if (typeof navigator.vibrate !== "function") return; // iOS Safari, most desktop
  if (!isPrimaryInputTouch()) return; // desktop gate
  if (prefersReducedMotion()) return; // §12.10: suppressed entirely
  try {
    navigator.vibrate(HAPTIC_DURATION_MS);
  } catch {
    // Some browsers throw for a vibrate call outside a user gesture, or in
    // an iframe without the right permissions policy. Bonus channel: swallow.
  }
}
